import React, { FC, useMemo } from "react";

import ReactMarkdown from "react-markdown";
import type {
    HeadingProps,
    SpecialComponents,
} from "react-markdown/lib/ast-to-react";
import type { NormalComponents } from "react-markdown/lib/complex-types";
import type { ReactMarkdownOptions } from "react-markdown/lib/react-markdown";

import styled from "@emotion/styled";
import classNames from "classnames";

import HeadlineItem from "../Elements/ContentElement/HeadlineItem";
import Video from "../Elements/Video";
import { Link } from "../LinkComponent";

const h = (headline: 1 | 2 | 3 | 4 | 5 | 6) => {
    const Headline: FC<HeadingProps> = ({ children: name }) => (
        <HeadlineItem {...{ headline }}>{name}</HeadlineItem>
    );

    return Headline;
};

const Strong = styled.strong`
    color: ${({ theme }) => theme.colors.blue.toString()} !important;
`;

const defaultComponents: Partial<
    Omit<NormalComponents, keyof SpecialComponents> & SpecialComponents
> = {
    a: ({ href, ...props }: JSX.IntrinsicElements["a"]) => (
        <Link to={href as string} {...props} />
    ),
    img: ({ src, alt, ...props }: JSX.IntrinsicElements["img"]) =>
        src &&
        /^https:\/\/(?:www\.)?youtube(?:-nocookie)\.com\/(?:watch\?v=|embed\/)(?<videoId>[\w_-]+)$/.test(
            src
        ) ? (
            <Video url={src} {...props} />
        ) : (
            <img {...{ src, alt, ...props }} />
        ),
    h1: h(1),
    h2: h(2),
    h3: h(3),
    h4: h(4),
    h5: h(5),
    h6: h(6),
    strong: ({ children }) => <Strong>{children}</Strong>,
};

export type MarkdownProps = ReactMarkdownOptions & {
    condensedText?: boolean;
};

const Markdown: FC<MarkdownProps> = ({
    components,
    condensedText = false,
    ...props
}) => {
    const components_ = useMemo(
        () => ({ ...defaultComponents, ...components }),
        [components]
    );

    return <ReactMarkdown components={components_} {...props} />;
};

const MarkdownBlock: FC<MarkdownProps> = ({ className, ...props }) => (
    <Markdown className={classNames(className)} {...props} />
);
export default MarkdownBlock;

export const MarkdownInline: FC<
    Omit<MarkdownProps, "allowedElements" | "unwrapDisallowed">
> = (props) => (
    <Markdown
        allowedElements={["br", "strong", "em", "a", "span"]}
        unwrapDisallowed
        {...props}
    />
);

const Paragraph = styled.p`
    padding: 3px !important;
    margin: 0 !important;
`;

export const MarkdownWithLessParagraphPadding: FC<MarkdownProps> = ({
    components,
    ...props
}) => {
    const _paragraph: typeof defaultComponents = {
        p: ({ children }) => <Paragraph>{children}</Paragraph>,
    };
    const components_ = useMemo(
        () => ({ ...defaultComponents, ...components, ..._paragraph }),
        [components]
    );
    return <ReactMarkdown components={components_} {...props} />;
};
